/*
 * File: WeSpeakAccessibilityStatement.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on April 9, 2025 at 5:09 PM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 31, 2025 at 10:03 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// Components
import {
	Meta,
	AppNavigation,
	Typography,
	LegalNavigation,
	LocaleLink,
} from '../../../../../src/components';
import { HighlightSection } from '../../../../../src/components/LegalNavigation/LegalNavigation.styles';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../../../constants/base.constants';
import { LEGAL_NAVIGATION_SECTIONS } from '../../Constants';

// Styles
import * as S from './WeSpeakAccessibilityStatement.styles';


/**
 * Component
 */

const WeSpeakAccessibilityStatement = ({ meta: m }) => {

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Define meta content
	const meta = useMemo(() => ({
		title: t('pageWeSpeakAccessibilityStatement.metaTitle', { partnerName: partner.name }),
		description: t('pageWeSpeakAccessibilityStatement.metaDescription', { partnerName: partner.name }),
		bodyClasses: 'hide-captcha',
		...m || {}
	}), [m, partner, t]);

	// Create navigation sections
	const navigationSections = useMemo(() => LEGAL_NAVIGATION_SECTIONS(locale), [locale]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} data={{}} />

			{/* Component Content */}
			<AppNavigation showFooter contentPadding={false}>
				<LegalNavigation
					navigationSections={navigationSections}
					mobileNavigationHeight={440}
				>
					<S.Wrapper>

						{/* Title */}
						<Typography tag="h1" weight="bold">
							We-Speak Accessibility Commitment
						</Typography>

						{/* TL;DR */}
						<HighlightSection>
							<Typography tag="p">
								The We-Speak platform is powered by
								{' '}
								<LocaleLink
									title="Our Wave"
									aria-label="Our Wave"
									target="_blank"
									to="https://www.ourwave.org"
									rel="noreferrer"
								>
									Our Wave
								</LocaleLink>
								, a nonprofit
								organization dedicated to building meaningful technology to empower survivors of sexual violence. For more
								information about our technology, team, or partnership opportunities, please
								{' '}
								<LocaleLink
									title="Visit Our Website"
									aria-label="Visit Our Website"
									target="_blank"
									to="https://www.ourwave.org"
									rel="noreferrer"
								>
									visit our website
								</LocaleLink>
								.
							</Typography>
						</HighlightSection>

						{/* General */}
						<Typography tag="h2" weight="bold">General</Typography>
						<Typography tag="p">
							We-Speak is committed to ensuring that all of our websites and services are accessible to all users, irrespective of
							device in use, technology, or ability. Our platforms have been built to provide maximum accessibility and usability to
							all visitors. As a result, our websites can be viewed from a variety of devices, including desktop and laptop computers,
							web-enabled mobile devices, and many others. We-Speak and Our Wave have invested a significant amount of resources to
							help ensure that our websites and services are made easier to use and more accessible for people with disabilities,
							with the strong belief that every person has the right to live with dignity, equality, comfort and independence.
						</Typography>
						<br />

						{/* Disclaimer */}
						<Typography tag="h2" weight="bold">Disclaimer</Typography>
						<Typography tag="p">
							We-Speak continues our efforts to constantly improve the accessibility of our websites and services in the belief that
							it is our collective moral obligation to allow seamless, accessible, and unhindered use for those of us with
							disabilities. Despite our efforts to make all pages and content on We-Speak fully accessible, some content may not
							have yet been fully adapted to the strictest accessibility standards. This may be a result of not having found or
							identified the most appropriate technological solution.
						</Typography>
						<br />

						{/* We're Here For You */}
						<Typography tag="h2" weight="bold">We&apos;re Here For You</Typography>
						<Typography tag="p">
							If you are experiencing difficulty with any content on We-Speak or require assistance with any part of our website,
							please contact us during normal business hours as detailed below and we will be happy to assist.
						</Typography>
						<br />

						{/* Contact Us */}
						<Typography tag="h2" weight="bold">Contact Us</Typography>
						<Typography tag="p">
							If you wish to report an accessibility issue, have any questions, or need assistance, please contact We-Speak User
							Support by sending an email to info@weconsent.ie
						</Typography>

						{/* Updated At */}
						<Typography tag="p">
							<i>Updated May 31, 2025</i>
						</Typography>

					</S.Wrapper>
				</LegalNavigation>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

WeSpeakAccessibilityStatement.propTypes = {
	meta: PropTypes.shape()
};
WeSpeakAccessibilityStatement.defaultProps = {
	meta: {}
};


/**
 * Exports
 */

export default WeSpeakAccessibilityStatement;
